<script setup>
// Select z wyszukiwarką — zastępuje natywny `<select>` wszędzie w v2. Wygląda jak
// `.v2-input.v2-select`, ale lista jest filtrowana wpisywaniem, bo katalogi
// (posiłki, produkty, kategorie) bywają za długie, żeby ich szukać oczami.
// Obsługuje opcje płaskie [{value,label}] i pogrupowane [{label,items:[...]}].
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { looseIncludes } from '@/composables/useV2Text.js';

const { t } = useI18n();

const props = defineProps({
    modelValue: { type: [String, Number, null], default: null },
    options: { type: Array, default: () => [] },
    // Etykieta pustego wyboru; brak = nie da się wybrać „nic".
    emptyLabel: { type: String, default: null },
    placeholder: { type: String, default: null },   // brak = „Wybierz"
    searchPlaceholder: { type: String, default: null },   // brak = „Szukaj…"
    id: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    invalid: { type: Boolean, default: false },
});
const emit = defineEmits(['update:modelValue']);

// Krótką listę widać całą, więc pole szukania byłoby tam tylko szumem.
const SEARCH_FROM = 8;

const open = ref(false);
const query = ref('');
const cursor = ref(0);
const root = ref(null);
const input = ref(null);

const isGrouped = computed(() => props.options.some(o => Array.isArray(o?.items)));

const optionCount = computed(() => (isGrouped.value
    ? props.options.reduce((a, g) => a + (g.items?.length ?? 0), 0)
    : props.options.length));

const searchable = computed(() => optionCount.value >= SEARCH_FROM);

// Wspólny kształt dla obu wariantów: płaska lista wyborów + nagłówki grup.
const entries = computed(() => {
    const out = [];
    if (props.emptyLabel) out.push({ kind: 'option', value: null, label: props.emptyLabel });
    if (isGrouped.value) {
        for (const g of props.options) {
            out.push({ kind: 'group', label: g.label });
            for (const o of g.items ?? []) out.push({ kind: 'option', value: o.value ?? o.key, label: o.label });
        }
    } else {
        for (const o of props.options) out.push({ kind: 'option', value: o.value, label: o.label, hint: o.hint });
    }
    return out;
});

const filtered = computed(() => {
    const term = query.value.trim();
    if (!term) return entries.value;
    const kept = entries.value.filter(e => e.kind === 'option' && looseIncludes(e.label, term));
    // Nagłówek grupy zostaje tylko wtedy, gdy coś z niej przeszło filtr.
    if (!isGrouped.value) return kept;
    const out = [];
    for (const e of entries.value) {
        if (e.kind === 'group') {
            const idx = entries.value.indexOf(e);
            const following = entries.value.slice(idx + 1);
            const until = following.findIndex(x => x.kind === 'group');
            const members = until === -1 ? following : following.slice(0, until);
            if (members.some(m => kept.includes(m))) out.push(e);
        } else if (kept.includes(e)) out.push(e);
    }
    return out;
});

const selectable = computed(() => filtered.value.filter(e => e.kind === 'option'));

const selectedLabel = computed(() => {
    const found = entries.value.find(e => e.kind === 'option' && e.value === props.modelValue);
    return found?.label ?? null;
});

const close = () => { open.value = false; query.value = ''; };

const toggle = async () => {
    if (props.disabled) return;
    open.value = !open.value;
    if (!open.value) { query.value = ''; return; }
    query.value = '';
    cursor.value = Math.max(0, selectable.value.findIndex(e => e.value === props.modelValue));
    if (!searchable.value) return;
    await nextTick();
    input.value?.focus();
};

// Bez pola szukania klawiaturę obsługuje sam przycisk — inaczej strzałki
// i Enter nie miałyby gdzie trafić.
const onButtonKeydown = (event) => {
    if (!open.value) {
        if (['ArrowDown', 'Enter', ' '].includes(event.key)) { event.preventDefault(); toggle(); }
        return;
    }
    if (searchable.value) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); move(1); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); move(-1); }
    else if (event.key === 'Enter') { event.preventDefault(); pick(selectable.value[cursor.value]); }
    else if (event.key === 'Escape') { event.preventDefault(); close(); }
};

const pick = (entry) => {
    emit('update:modelValue', entry.value);
    close();
};

const move = (delta) => {
    const count = selectable.value.length;
    if (!count) return;
    cursor.value = (cursor.value + delta + count) % count;
};

watch(filtered, () => { cursor.value = 0; });

const onPointerDown = (event) => {
    if (open.value && !root.value?.contains(event.target)) close();
};

onMounted(() => document.addEventListener('mousedown', onPointerDown));
onUnmounted(() => document.removeEventListener('mousedown', onPointerDown));
</script>

<template>
    <div ref="root" class="relative">
        <button
            :id="id"
            type="button"
            role="combobox"
            :aria-expanded="open"
            aria-haspopup="listbox"
            :aria-invalid="invalid"
            :disabled="disabled"
            class="v2-input v2-select text-left flex items-center disabled:opacity-60"
            :class="selectedLabel ? 'text-[#211E1A]' : 'text-[#B8B0A3]'"
            @click="toggle"
            @keydown="onButtonKeydown"
        >
            <span class="flex-1 min-w-0 truncate">{{ selectedLabel ?? placeholder ?? t('common.select.placeholder') }}</span>
        </button>

        <div
            v-if="open"
            class="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-[#E4DDD1] rounded-xl shadow-[0_12px_28px_-14px_rgba(33,30,26,0.35)] overflow-hidden"
        >
            <div v-if="searchable" class="flex items-center gap-2 px-3 h-9 border-b border-[#EFE9DF]">
                <i class="pi pi-search text-[10px] text-[#B8B0A3]"></i>
                <input
                    ref="input"
                    v-model="query"
                    type="text"
                    :placeholder="searchPlaceholder ?? t('common.select.search')"
                    :aria-label="searchPlaceholder ?? t('common.select.search')"
                    class="flex-1 min-w-0 bg-transparent border-none text-[13px] text-[#211E1A] placeholder:text-[#B8B0A3] focus:outline-none"
                    @keydown.down.prevent="move(1)"
                    @keydown.up.prevent="move(-1)"
                    @keydown.enter.prevent="pick(selectable[cursor])"
                    @keydown.esc.prevent="close"
                    @keydown.tab="close"
                />
            </div>

            <ul v-if="filtered.length" role="listbox" class="max-h-[220px] overflow-y-auto py-1">
                <template v-for="(e, i) in filtered" :key="`${e.kind}-${e.label}-${i}`">
                    <li
                        v-if="e.kind === 'group'"
                        class="px-3 pt-2 pb-1 text-[9px] font-bold tracking-[0.16em] text-[#A29A8E]"
                    >{{ e.label }}</li>
                    <li v-else>
                        <button
                            type="button"
                            role="option"
                            :aria-selected="e.value === modelValue"
                            class="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[13px] transition-colors"
                            :class="[
                                selectable.indexOf(e) === cursor ? 'bg-[#F2ECE1]' : 'hover:bg-[#FBF9F4]',
                                e.value === modelValue ? 'text-[#211E1A] font-semibold' : 'text-[#4A443C]',
                            ]"
                            @mouseenter="cursor = selectable.indexOf(e)"
                            @click="pick(e)"
                        >
                            <span class="flex-1 min-w-0 truncate">{{ e.label }}</span>
                            <span v-if="e.hint" class="text-[11.5px] text-[#A29A8E] shrink-0">{{ e.hint }}</span>
                            <i v-if="e.value === modelValue" class="pi pi-check text-[9px] text-[#C0764B] shrink-0"></i>
                        </button>
                    </li>
                </template>
            </ul>
            <p v-else class="px-3 py-3 text-[12.5px] text-[#8A8378]">{{ t('common.select.noMatch') }}</p>
        </div>
    </div>
</template>
